/**
 * JS Object list (WS-2) — the rail's "JS" context: the page's JS Objects, plus
 * "+ JS Object". Selecting one opens its full-height editor as a workspace tab
 * (`JsObjectEditorPane`); the list here is just the picker. JS Objects are keyed
 * by NAME (they have no id) — the binding language addresses them by name.
 */
import { Braces, Plus } from "lucide-react";

import type { PageJsObject } from "@/lib/pageTypes";

export function JsObjectListPanel({
  jsObjects,
  activeName,
  onSelect,
  onAdd,
}: {
  jsObjects: PageJsObject[];
  /** The JS Object whose tab is currently active (highlighted), or null. */
  activeName: string | null;
  onSelect: (name: string) => void;
  onAdd: () => void;
}) {
  const list = Array.isArray(jsObjects) ? jsObjects : [];
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={onAdd}
        className="mb-1 flex items-center justify-center gap-1.5 rounded-md border border-dashed border-border px-2 py-1.5 text-xs text-muted-foreground hover:border-accent/60 hover:text-foreground"
      >
        <Plus className="h-3.5 w-3.5" strokeWidth={1.5} /> JS Object
      </button>

      {list.length === 0 ? (
        <p className="px-1 py-2 text-[11px] text-muted-foreground">
          Nessun JS Object. Crea funzioni client riusabili, chiamabili dai binding come{" "}
          <code className="rounded bg-muted px-1">{"{{ Utils.fn() }}"}</code>.
        </p>
      ) : (
        list.map((o) => {
          const active = o.name === activeName;
          return (
            <button
              key={o.name}
              type="button"
              onClick={() => onSelect(o.name)}
              className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] ${
                active ? "bg-accent/15 text-accent" : "text-foreground hover:bg-surface"
              }`}
              title={o.name}
            >
              <Braces className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
              <span className="min-w-0 flex-1 truncate font-medium">{o.name}</span>
            </button>
          );
        })
      )}
    </div>
  );
}
